<template>
  <new-edit-page
    :page-title="pageTitle(actionType, 'ClientRole')"
    :submit-button-label="submitButtonLabel(actionType)"
    :submit-button-disabled="submitButtonDisabled"
    :is-loading="loading"
    @submit="submit"
  >
    <template v-slot:leftSideContent>
      <v-layout row justify-space-between wrap>
        <div class="input-field-container md-large">
          <label class="required" for="label">
            {{ $tc("Label", 1) }}
          </label>
          <input
            :value="role.label"
            @input="updateField('label', $event.target.value)"
            @change="validateString('badLabel', role.label)"
            id="label"
            class="input-field"
            :class="{ fieldErrorInput: validation.badLabel }"
          />
          <div v-if="validation.badLabel" class="field-error-div">
            {{ $t("ValidLabel") }}
          </div>
        </div>

        <div class="input-field-container large">
          <label for="description">{{ $t("Description") }}</label>
          <textarea
            :value="role.description"
            @input="updateField('description', $event.target.value)"
            id="description"
            class="input-field"
          />
        </div>

        <div class="input-field-container radio-container medium">
          <label class="required" for="active">{{ $t("Status") }}</label>
          <v-radio-group
            id="active"
            :value="role.active"
            @change="updateField('active', $event)"
            :row="true"
          >
            <v-radio
              :label="$t('Active')"
              :value="true"
              :ripple="false"
              color="#4fa2b7"
            />
            <v-radio
              :label="$t('Inactive')"
              :value="false"
              :ripple="false"
              color="#4fa2b7"
            />
          </v-radio-group>
        </div>
        <div class="input-field-container medium" />

        <div class="input-field-container" :style="{ width: fieldWidth }">
          <v-switch
            color="accent"
            :input-value="role.visible"
            @change="updateField('visible', $event)"
            :label="role.visible ? $t('Visible') : $t('NotVisible')"
          />
        </div>
        <div class="input-field-container" :style="{ width: fieldWidth }">
          <v-switch
            color="accent"
            :input-value="role.blocked"
            @change="updateField('blocked', $event)"
            :label="role.blocked ? $t('Blocked') : $t('Unblocked')"
          />
        </div>
        <div class="input-field-container large" v-if="actionType === 'create'">
          <label> {{ $t("BaseRole") }} </label>
          <treeselect
            :value="baseRoleId"
            :options="roleOptions"
            :load-options="loadRoleOptions"
            @input="baseRoleId = $event"
          />
        </div>
      </v-layout>
    </template>

    <template v-slot:rightSideHeader>
      <management-tabs-and-search
        :tabs="tabs"
        :open-tab="openTab"
        @update-open-tab="openTab = $event"
        :search="search"
        @update-search="search[$event.tab] = $event.value"
      />
    </template>

    <template v-slot:rightSideContent>
      <v-tabs-items v-model="openTab">
        <v-tab-item value="permissions">
          <permissions-section
            account-type="clientRole"
            :previousRolesAndPermissions="previousRolesAndPermissions"
            :has-permission-to-edit="true"
            :type="actionType"
            @selectedPermissions="updateField('permissions', $event)"
          />
        </v-tab-item>
        <v-tab-item value="webUsers">
          <associated-users
            :action-type="actionType"
            user-type="web"
            from="role"
            :associated-users="role.usersWebAssociates"
            @associate-users="updateField('usersWebAssociates', $event)"
            :has-permission-to-edit="hasUsersPermission"
            :search-from-parent="search.webUsers"
          />
        </v-tab-item>

        <v-tab-item value="mobileUsers">
          <associated-users
            :action-type="actionType"
            user-type="mobile"
            from="role"
            :associated-users="role.usersMobileAssociates"
            @associate-users="updateField('usersMobileAssociates', $event)"
            :has-permission-to-edit="hasUsersPermission"
            :search-from-parent="search.mobileUsers"
          />
        </v-tab-item>
        <v-tab-item value="companies">
          <associated-companies
            from="role"
            :action-type="actionType"
            :associated-companies="role.companies"
            @associate-companies="updateField('companies', $event)"
            :has-permission-to-edit="hasCompaniesPermission"
            :search-from-parent="search.companies"
          />
        </v-tab-item>
      </v-tabs-items>
    </template>
  </new-edit-page>
</template>

<script>
import { createEditPageMixin } from "@/mixins/management/createEditPageMixin";
import NewEditPage from "@/components/Management/NewEditPage/NewEditPage";
import ManagementTabsAndSearch from "@/components/_Organisms/ManagementTabsAndSearch";
import PermissionsSection from "@/components/SystemManagement/Accounts/Permissions/Permissions";
import AssociatedUsers from "@/components/SystemManagement/Accounts/AssociatedUsers";
import AssociatedCompanies from "@/components/SystemManagement/Accounts/AssociatedCompanies";
import { mapActions } from "vuex";

export default {
  name: "RoleForm",

  components: {
    AssociatedCompanies,
    AssociatedUsers,
    PermissionsSection,
    ManagementTabsAndSearch,
    NewEditPage
  },

  mixins: [createEditPageMixin],

  props: {
    /**
     * Has permission to edit the permissions associated with the role client
     */
    hasPermissionsPermission: Boolean,

    /**
     * Has permission to edit the companies associated with the client role
     */
    hasCompaniesPermission: Boolean,

    /**
     * Has permission to edit the users associated with the client role
     */
    hasUsersPermission: Boolean,

    /**
     * Object to be edited or created
     */
    role: {
      type: Object,
      required: true
    },

    /**
     * Define if the page is loading
     */
    loading: Boolean,

    /**
     * Permissions that were on the client role selected separated by roles
     */
    previousRolesAndPermissions: Array
  },

  data() {
    return {
      /**
       * Validation of mandatory fields
       */
      validation: {
        badLabel: false
      },

      /**
       * Determines which tab is being displayed.
       */
      openTab: "permissions",

      /**
       * Saves the value of the respective search of each tab
       */
      search: {
        webUsers: "",
        mobileUsers: "",
        companies: ""
      },

      /**
       * Client options roles that can be used as a basis for creating a new one-º
       */
      roleOptions: null,

      /**
       * Client role id used as base
       */
      baseRoleId: undefined
    };
  },

  computed: {
    /**
     * Defines the width of the fields
     * @return {string}
     */
    fieldWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
        case "md":
          return "100%";
        default:
          return "45%";
      }
    },

    /**
     * Defines the tabs that appear on the left side
     * {String} value - That will be used to define which tab will be displayed
     * {String} text - Tab title
     * {Boolean} hasSearch - Defines if the tab will have the search bar
     */
    tabs() {
      return [
        {
          value: "permissions",
          text: this.$i18n.tc("Permission", 2)
        },
        {
          value: "webUsers",
          text: this.$i18n.tc("User", 2) + ": Web",
          hasSearch: true
        },
        {
          value: "mobileUsers",
          text: this.$i18n.tc("User", 2) + ": Mobile",
          hasSearch: true
        },
        {
          value: "companies",
          text: this.$i18n.tc("Company", 2),
          hasSearch: true
        }
      ];
    }
  },

  watch: {
    /**
     * Whenever the baseRoleId is changed, the getPreviousRolesAndPermissions method will be executed to send the
     * roles separated permissions to the parent component
     *
     */
    async baseRoleId(newValue) {
      let baseRole = this.roleOptions.find(role => role.id === newValue);
      for (const [key, value] of Object.entries(baseRole)) {
          if (key !== "permissions" && key !== "label" && key !== "description") {
          this.updateField(key, value);
        }
      }
      if (baseRole && baseRole.permissions) {
        let permissions = await this.getPreviousRolesAndPermissions(
          baseRole.permissions
        );

        /**
         * Send client role base permissions by roles
         *
         * @property {Array} newValue new value set
         * @event change-previous-roles-and-permissions
         */
        this.$emit("change-previous-roles-and-permissions", permissions);
      }
    }
  },

  methods: {
    ...mapActions(["fetchAllClientRoles", "fetchAllPermissions"]),

    /**
     * Load the client roles that can be used as a base
     * @return sets the value of the roleOptions variable
     */
    async loadRoleOptions() {
      try {
        let clientRoleOptions = await this.fetchAllClientRoles(
          "users,permissions"
        );

        // separate the users of each client role by type
        clientRoleOptions.forEach(clientRole => {
          clientRole.usersMobileAssociates = [];
          clientRole.usersWebAssociates = [];
        });
        this.roleOptions = clientRoleOptions;
      } catch (error) {
        console.error(error);
      }
    },

    /**
     * Separate the permissions associated with the selected client role by roles
     *
     * @param {Array} clientRolePermissions - Permissions for the selected client role
     * @return {Promise<[{permissions: [], name: string}, {permissions: [], name: string},
     *                   {permissions: [], name: string}, {permissions: [], name: string}]>}
     */
    async getPreviousRolesAndPermissions(clientRolePermissions) {
      let roles = [
        { name: "management", permissions: [] },
        { name: "fibercloud_web", permissions: [] },
        { name: "fibercloud_mobile", permissions: [] },
        { name: "modules", permissions: [] }
      ];

      let allPermissions = await this.fetchAllPermissions("roles");

      //separate client role permissions by roles
      clientRolePermissions.forEach(clientRolePermission => {
        let permission = allPermissions.find(
          permission => permission.id === clientRolePermission.id
        );
        let role = roles.find(role => role.name === permission.roles[0].name);
        let index = roles.indexOf(role);
        roles[index].permissions.push(clientRolePermission);
      });

      return roles;
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SystemManagement/Accounts/ClientRoles/ClientRoleForm.vue